import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useSpeckleStore, useSpeckleAuthStore } from '@haskoning/vue-speckle'

import { useCalculationStore } from '@/stores/calculation'
import { useProjectSpeckleStore } from '@/stores/projectSpeckle'
import { Calculation } from '@/types/calcualtion.type'

export const useSpeckleManagement = () => {
  const speckleStore = useSpeckleStore({ autoSelectData: false })
  const speckleExportLoading = ref(false)
  const projectSpeckleStore = useProjectSpeckleStore()
  const calculationStore = useCalculationStore()
  const speckleAuthStore = useSpeckleAuthStore()

  const { message } = storeToRefs(projectSpeckleStore)

  const { currentItem: currentCalculation } = storeToRefs(calculationStore)

  watch(
    () => speckleStore.currentSelection.project,
    async (newValue) => {
      if (!newValue) return
      await setModelInfo()
      await projectSpeckleStore.updateProjectSpeckle()
    }
  )

  watch(
    () => speckleStore.currentSelection.model,
    async (newValue) => {
      if (!newValue) return
      const calculationId = currentCalculation.value?.id
      if (calculationId) {
        const currentSelectedModelId = currentCalculation.value?.selected_speckle_model_id
        const selectedModelId = newValue.id
        if (currentSelectedModelId === selectedModelId) return
        await linkModelToCalculation(newValue.id, calculationId)
      }
    }
  )

  async function linkModelToCalculation(speckleModelId: string, calculationId: string) {
    if (calculationId) {
      await calculationStore.update({
        id: calculationId,
        selected_speckle_model_id: speckleModelId
      })
    }
  }

  async function createAndLinkDefaultModel(speckleProjectId: string, calculation: Calculation) {
    const modelName = calculation.name
    const calculationId = calculation.id
    const newModel = await projectSpeckleStore.addDefaultModel(speckleProjectId, modelName)
    if (newModel) {
      await linkModelToCalculation(newModel.id, calculationId)
    }
    return newModel
  }

  async function setModelInfo() {
    await speckleStore.getSpeckleModels()
    if (currentCalculation.value?.selected_speckle_model_id) {
      const selectedModelId = currentCalculation.value.selected_speckle_model_id
      const model = speckleStore.allModels.find((m) => m.id === selectedModelId)
      if (model) {
        speckleStore.currentSelection.model = model
        return
      }
    } else {
      const speckleProjectId = speckleStore.currentSelection.project?.id
      const modelName = currentCalculation.value?.name
      if (speckleProjectId) {
        await createAndLinkDefaultModel(speckleProjectId, currentCalculation.value)
      }
      for (const model of speckleStore.allModels) {
        if (model.name === modelName) {
          speckleStore.currentSelection.model = model
          return
        }
      }
      speckleStore.currentSelection.model = undefined
    }
  }

  const isValidInput = computed(() => {
    return [
      message.value,
      speckleStore.currentSelection.project,
      speckleStore.currentSelection.model
    ].every((item) => Boolean(item))
  })

  async function exportCalculationToSpeckle() {
    if (!isValidInput.value) {
      return
    }
    try {
      speckleExportLoading.value = true
      await projectSpeckleStore.send(currentCalculation.value.id, message.value)
    } finally {
      speckleExportLoading.value = false
    }
  }

  onMounted(async () => {
    if (speckleAuthStore.isAuthenticated && (await speckleAuthStore.checkTokenValidity())) {
      await speckleStore.getAllUserProjects()
    }
  })

  return {
    isValidInput,
    canUseSpeckle: !!speckleStore.canUseSpeckle,
    currentSelection: speckleStore.currentSelection,
    exportCalculationToSpeckle,
    setModelInfo,
    createAndLinkDefaultModel,
    linkModelToCalculation,
    speckleExportLoading
  }
}
